import { type AppDispatch } from 'common/hooks/redux'
import { mergeDefined } from 'common/utils/mergeDefined'
import dayjs from 'dayjs'
import { type IPatientStatus } from 'features/Home/interfaces/IInfoSchedule'
import {
  classifyPendingPatientUpsert,
  type PendingPatientPayloadKind,
  toScheduleSlotSnapshot,
} from 'features/Schedule/pusher/schedulePusherEcho'
import { type PatientAppointmentType } from 'features/Schedule/pusher/type'
import _ from 'lodash'
import { scheduleApi } from './schedule.api'
import type { ScheduleAppointmentDto, ScheduleDay, ScheduleWeek } from './schedule.types'

export type PatchResult = { undo: () => void }

export type ScheduleCacheContext = {
  siteId: string
  viewDate: string
  scheduleQueries: Record<string, unknown>
}

type ScheduleDraftScope =
  | { kind: 'day'; date: string }
  | { kind: 'week'; startDate: string; endDate: string }

const appointmentBelongsInScope = (appointmentDate: string, scope: ScheduleDraftScope): boolean => {
  if (scope.kind === 'day') {
    return appointmentDate === scope.date
  }
  return appointmentDate >= scope.startDate && appointmentDate <= scope.endDate
}

const isNotOlderThan = (incoming?: string | null, current?: string | null): boolean => {
  if (!incoming || !current) return true
  const incomingAt = dayjs(incoming)
  const currentAt = dayjs(current)
  if (!incomingAt.isValid() || !currentAt.isValid()) return true
  return incomingAt.valueOf() >= currentAt.valueOf()
}

const resolveRoomCode = (
  patientData:
    | Pick<PatientAppointmentType, 'room'>
    | { room?: { code?: string; value?: string } | string },
): string | undefined => {
  const room = patientData.room as { code?: string; value?: string } | string | undefined
  if (!room) return undefined
  if (typeof room === 'string') return room
  return room.code ?? room.value
}

const findAppointmentIndex = (
  items: ScheduleDay['items'],
  appointmentId: number | string,
): number =>
  items.findIndex((item) => item.id === appointmentId || Number(item.id) === Number(appointmentId))

const mergeDecorationOnly = (
  existing: ScheduleDay['items'][number],
  patientData: PatientAppointmentType,
) => {
  const decoration = _.omit(patientData, ['date', 'start_time', 'end_time', 'room'])
  const merged = {
    ...mergeDefined(
      existing as unknown as Record<string, unknown>,
      decoration as unknown as Record<string, unknown>,
    ),
    room: existing.room,
    // Never let a sparse pusher/API payload wipe colour inputs.
    status: patientData.status ?? existing.status,
    category: patientData.category ?? existing.category,
  } as typeof existing

  if (!isNotOlderThan(patientData.updated_at, existing.updated_at)) {
    merged.updated_at = existing.updated_at
  }
  return merged
}

const hasPositionChange = (
  existing: ScheduleDay['items'][number],
  patientData: PatientAppointmentType,
): boolean => {
  const current = toScheduleSlotSnapshot(existing)
  const incoming = toScheduleSlotSnapshot(patientData)
  const incomingRoomCode = resolveRoomCode(patientData)

  return (
    (patientData.start_time != null && incoming.start_time !== current.start_time) ||
    (patientData.end_time != null && incoming.end_time !== current.end_time) ||
    (patientData.date != null && incoming.date !== current.date) ||
    (incomingRoomCode != null && incomingRoomCode !== '' && incoming.room !== current.room)
  )
}

/**
 * Last-write-wins on `updated_at`, with two exceptions:
 * - A concurrent remote move still applies while our own PUT is in flight.
 * - Our PUT response takes the slot back only if it is not older than cache.
 * EHR confirmation never moves the block, and must not steal the LWW clock.
 */
export const shouldApplyStructuralPatientFields = ({
  allowStructural,
  isEmrSyncComplete,
  timestampOk,
  positionChanged,
  pendingPayload = 'none',
}: {
  allowStructural: boolean
  isEmrSyncComplete: boolean
  timestampOk: boolean
  positionChanged: boolean
  pendingPayload?: PendingPatientPayloadKind
}): boolean => {
  if (isEmrSyncComplete) return false
  if (pendingPayload === 'stale' || pendingPayload === 'intended') return false

  if (allowStructural) {
    if (positionChanged && pendingPayload === 'remote') return true
    if (pendingPayload === 'remote') return false
    return timestampOk
  }

  return timestampOk && positionChanged
}

export const mergeIncomingPatientAppointment = (
  existing: ScheduleDay['items'][number],
  patientData: PatientAppointmentType,
  room: ScheduleDay['items'][number]['room'] | undefined,
  { allowStructural }: { allowStructural: boolean },
): ScheduleDay['items'][number] => {
  const timestampOk = isNotOlderThan(patientData.updated_at, existing.updated_at)
  const incomingEmr = patientData.emr_updated_at ?? null
  const existingEmr = existing.emr_updated_at ?? null
  const isEmrSyncComplete = !!incomingEmr && incomingEmr !== existingEmr
  const applyStructural = shouldApplyStructuralPatientFields({
    allowStructural,
    isEmrSyncComplete,
    timestampOk,
    positionChanged: hasPositionChange(existing, patientData),
    pendingPayload: classifyPendingPatientUpsert(patientData),
  })

  if (!applyStructural) {
    const merged = mergeDecorationOnly(existing, patientData)
    if (isEmrSyncComplete) {
      merged.updated_at = existing.updated_at
    }
    return merged
  }

  return {
    ...mergeDefined(
      existing as unknown as Record<string, unknown>,
      patientData as unknown as Record<string, unknown>,
    ),
    room: room ?? existing.room,
    status: patientData.status ?? existing.status,
    category: patientData.category ?? existing.category,
  } as typeof existing
}

const upsertPatientInDraft = (
  draft: ScheduleDay | ScheduleWeek,
  patientData: PatientAppointmentType,
  scope: ScheduleDraftScope,
  allowStructural: boolean,
): boolean => {
  if (!draft?.items) return false

  const index = findAppointmentIndex(draft.items, patientData.id)
  const existing = index === -1 ? undefined : draft.items[index]
  const pendingPayload = classifyPendingPatientUpsert(patientData)
  const timestampOk = !existing || isNotOlderThan(patientData.updated_at, existing.updated_at)
  const incomingEmr = patientData.emr_updated_at ?? null
  const existingEmr = existing?.emr_updated_at ?? null
  const isEmrSyncComplete = !!existing && !!incomingEmr && incomingEmr !== existingEmr
  const positionChanged = !!existing && hasPositionChange(existing, patientData)
  const structuralAllowed = shouldApplyStructuralPatientFields({
    allowStructural,
    isEmrSyncComplete,
    timestampOk,
    positionChanged,
    pendingPayload,
  })

  if (!appointmentBelongsInScope(patientData.date, scope)) {
    if (!existing) return false
    if (structuralAllowed) {
      draft.items.splice(index, 1)
      return true
    }
  }

  const roomCode = resolveRoomCode(patientData)
  const room = roomCode
    ? draft.rooms.find((r) => r.value === roomCode || (r as { code?: string }).code === roomCode)
    : undefined

  if (!existing) {
    if (!allowStructural) return false
    draft.items.push({
      ...patientData,
      room,
    })

    if ('has_ehr_booking' in draft && !patientData.emr_updated_at) {
      draft.has_ehr_booking = true
    }

    return true
  }

  const newObj = mergeIncomingPatientAppointment(existing, patientData, room, {
    allowStructural,
  })

  if (_.isEqual(existing, newObj)) {
    return false
  }

  draft.items.splice(index, 1, newObj)
  return true
}

const deletePatientInDraft = (
  draft: ScheduleDay | ScheduleWeek,
  appointmentId: number,
): boolean => {
  if (!draft?.items) return false
  const index = findAppointmentIndex(draft.items, appointmentId)
  if (index === -1) return false
  draft.items.splice(index, 1)
  return true
}

const updatePatientStatusInDraft = (
  draft: ScheduleDay | ScheduleWeek,
  appointmentId: number,
  status_code: number,
  temperature: number,
): boolean => {
  if (!draft?.items) return false

  const status = draft.patient_statuses.find((s) => s.code === status_code)
  if (!status) return false

  const index = findAppointmentIndex(draft.items, appointmentId)
  if (index === -1) return false

  const nextItem = {
    ...draft.items[index],
    status: status as IPatientStatus,
    temperature,
  }

  if (_.isEqual(draft.items[index], nextItem)) {
    return false
  }

  draft.items[index] = nextItem
  return true
}

const updatePatientLocationInDraft = (
  draft: ScheduleDay | ScheduleWeek,
  appointmentId: number,
  room_id: number,
): boolean => {
  if (!draft?.items) return false

  const room = draft.rooms.find((r) => r.id === room_id)
  const index = findAppointmentIndex(draft.items, appointmentId)
  if (index === -1 || !room) return false

  const nextItem = { ...draft.items[index], room }
  if (_.isEqual(draft.items[index], nextItem)) {
    return false
  }

  draft.items[index] = nextItem
  return true
}

const updateRoomStatusInDraft = (
  draft: ScheduleDay,
  room_id: number,
  room_status_code: number,
): boolean => {
  if (!draft?.rooms) return false

  const roomIndex = draft.rooms.findIndex((room) => room.id === room_id)
  if (roomIndex === -1) return false

  const status = draft.room_statuses.find((s) => s.code === room_status_code)
  const nextRoom = {
    ...draft.rooms[roomIndex],
    status,
  }

  if (_.isEqual(draft.rooms[roomIndex], nextRoom)) {
    return false
  }

  draft.rooms[roomIndex] = nextRoom
  return true
}

const patchAppointmentInDraft = (
  draft: ScheduleDay | ScheduleWeek,
  appointmentId: number,
  data: Partial<ScheduleAppointmentDto>,
  targetDate?: string,
) => {
  if (!draft?.items) return

  const index = findAppointmentIndex(draft.items, appointmentId)
  if (index === -1) return

  const existing = draft.items[index]
  const room = data.room ? draft.rooms.find((r) => r.value === data.room) : existing.room

  const updated = {
    ...existing,
    ...(targetDate && { date: targetDate }),
    ...(data.start_time && { start_time: data.start_time }),
    ...(data.end_time && { end_time: data.end_time }),
    ...(room && { room }),
    ...(data.consultation_staff !== undefined && { consultation_staff: data.consultation_staff }),
    ...(data.temperature_bypass !== undefined && {
      temperature_bypass: data.temperature_bypass,
    }),
    ...(data.title !== undefined && { title: data.title }),
    ...(data.comments !== undefined && { comments: data.comments }),
  }

  if (targetDate && targetDate !== existing.date) {
    draft.items.splice(index, 1)
    draft.items.push(updated)
  } else if (!_.isEqual(existing, updated)) {
    draft.items.splice(index, 1, updated)
  }
}

const applyToScheduleCaches = (
  dispatch: AppDispatch,
  { siteId, viewDate, scheduleQueries }: ScheduleCacheContext,
  mutateDay: (draft: ScheduleDay, date: string) => void,
  mutateWeek: (draft: ScheduleWeek, startDate: string, endDate: string) => void,
  extraDayDates: Array<string | undefined> = [],
): PatchResult[] => {
  const patches: PatchResult[] = []
  const dates = new Set<string>([viewDate])
  extraDayDates.forEach((date) => {
    if (date) dates.add(date)
  })
  getActiveDayQueries(scheduleQueries, siteId).forEach(({ date }) => dates.add(date))

  dates.forEach((date) => {
    patches.push(
      dispatch(
        scheduleApi.util.updateQueryData(
          'fetchScheduleDay',
          { siteId, date },
          (draft) => mutateDay(draft, date),
        ) as any,
      ),
    )
  })

  getActiveWeekQueriesForSite(scheduleQueries, siteId).forEach(({ startDate, endDate }) => {
    patches.push(
      dispatch(
        scheduleApi.util.updateQueryData(
          'fetchScheduleWeek',
          { siteId, startDate, endDate },
          (draft) => mutateWeek(draft, startDate, endDate),
        ) as any,
      ),
    )
  })

  return patches
}

export const applyPatientUpsert = (
  dispatch: AppDispatch,
  context: ScheduleCacheContext,
  patientData: PatientAppointmentType,
  { allowStructural = true }: { allowStructural?: boolean } = {},
) => {
  applyToScheduleCaches(
    dispatch,
    context,
    (draft, date) => {
      upsertPatientInDraft(draft, patientData, { kind: 'day', date }, allowStructural)
    },
    (draft, startDate, endDate) => {
      upsertPatientInDraft(
        draft,
        patientData,
        { kind: 'week', startDate, endDate },
        allowStructural,
      )
    },
    [patientData.date],
  )
}

export const applyPatientDelete = (
  dispatch: AppDispatch,
  context: ScheduleCacheContext,
  appointmentId: number,
): PatchResult[] =>
  applyToScheduleCaches(
    dispatch,
    context,
    (draft) => {
      deletePatientInDraft(draft, appointmentId)
    },
    (draft) => {
      deletePatientInDraft(draft, appointmentId)
    },
  )

export const applyPatientStatusUpdate = (
  dispatch: AppDispatch,
  context: ScheduleCacheContext,
  appointmentId: number,
  status_code: number,
  temperature: number,
) => {
  applyToScheduleCaches(
    dispatch,
    context,
    (draft) => {
      updatePatientStatusInDraft(draft, appointmentId, status_code, temperature)
    },
    (draft) => {
      updatePatientStatusInDraft(draft, appointmentId, status_code, temperature)
    },
  )
}

export const applyPatientLocationUpdate = (
  dispatch: AppDispatch,
  context: ScheduleCacheContext,
  appointmentId: number,
  room_id: number,
) => {
  applyToScheduleCaches(
    dispatch,
    context,
    (draft) => {
      updatePatientLocationInDraft(draft, appointmentId, room_id)
    },
    (draft) => {
      updatePatientLocationInDraft(draft, appointmentId, room_id)
    },
  )
}

export const applyRoomStatusUpdate = (
  dispatch: AppDispatch,
  context: ScheduleCacheContext,
  room_id: number,
  room_status_code: number,
) => {
  const { siteId, viewDate } = context

  dispatch(
    scheduleApi.util.updateQueryData('fetchScheduleDay', { siteId, date: viewDate }, (draft) => {
      updateRoomStatusInDraft(draft, room_id, room_status_code)
    }) as any,
  )
}

/** Apply an in-flight appointment change to RTK cache immediately (before API resolves). */
export const patchAppointmentOptimistic = (
  dispatch: AppDispatch,
  siteId: string,
  date: string,
  appointmentId: number,
  data: Partial<ScheduleAppointmentDto>,
  getState: () => any,
): PatchResult[] => {
  const patches: PatchResult[] = []
  const scheduleQueries = getState().schedule.queries

  const isDateChanged = (draft: ScheduleDay | ScheduleWeek) => {
    if (!draft.items) return false
    const index = findAppointmentIndex(draft.items, appointmentId)
    return index !== -1 && draft.items[index].date !== date
  }

  patches.push(
    dispatch(
      scheduleApi.util.updateQueryData('fetchScheduleDay', { siteId, date }, (draft) => {
        if (!draft?.items) return
        if (isDateChanged(draft)) {
          const index = findAppointmentIndex(draft.items, appointmentId)
          if (index !== -1) draft.items.splice(index, 1)
          return
        }
        patchAppointmentInDraft(draft, appointmentId, data, date)
      }) as any,
    ),
  )

  getActiveWeekQueries(scheduleQueries, siteId, date).forEach(({ startDate, endDate }) => {
    patches.push(
      dispatch(
        scheduleApi.util.updateQueryData(
          'fetchScheduleWeek',
          { siteId, startDate, endDate },
          (draft) => {
            patchAppointmentInDraft(draft, appointmentId, data, date)
          },
        ) as any,
      ),
    )
  })

  return patches
}

export const getActiveDayQueries = (
  queries: any,
  siteId: string,
): Array<{ date: string }> => {
  if (!queries) return []

  const days: Array<{ date: string }> = []
  Object.keys(queries).forEach((queryKey) => {
    if (!queryKey.startsWith('fetchScheduleDay')) return
    const query = queries[queryKey]
    if (query?.originalArgs?.siteId === siteId && typeof query.originalArgs.date === 'string') {
      days.push({ date: query.originalArgs.date })
    }
  })
  return days
}

export const getActiveWeekQueriesForSite = (
  queries: any,
  siteId: string,
): Array<{ startDate: string; endDate: string }> => {
  if (!queries) return []

  const weeks: Array<{ startDate: string; endDate: string }> = []
  Object.keys(queries).forEach((queryKey) => {
    if (!queryKey.startsWith('fetchScheduleWeek')) return
    const query = queries[queryKey]
    if (query?.originalArgs?.siteId === siteId) {
      weeks.push({
        startDate: query.originalArgs.startDate,
        endDate: query.originalArgs.endDate,
      })
    }
  })
  return weeks
}

export const getActiveWeekQueries = (
  queries: any,
  siteId: string,
  date: string,
): Array<{ startDate: string; endDate: string }> => {
  const targetDate = dayjs(date)

  return getActiveWeekQueriesForSite(queries, siteId).filter(({ startDate, endDate }) => {
    const start = dayjs(startDate)
    const end = dayjs(endDate)
    return targetDate.isSameOrAfter(start) && targetDate.isSameOrBefore(end)
  })
}
